import React, { useState } from 'react';
import { useSurfaceStyles } from '../assets/SurfaceStyleContext';
import { SURFACE_STYLE_GROUPS } from '../utils/surfaceStyleDefaults';

function Swatch({ color, opacity }) {
    return (
        <div style={{
            width: 28, height: 28, borderRadius: 6, flexShrink: 0,
            background: color, opacity: Math.max(opacity, 0.15),
            border: '1px solid rgba(255,255,255,0.15)',
        }} />
    );
}

function StyleRow({ groupKey, item, style, setStyle, resetStyle, isCustom }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '8px 4px', borderBottom: '1px solid rgba(255,255,255,0.04)',
        }}>
            <Swatch color={style.color} opacity={style.opacity} />
            <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--text-primary)' }}>{item.label}</div>
            <input
                type="color"
                value={style.color}
                onChange={e => setStyle(`${groupKey}.${item.key}`, { color: e.target.value })}
                style={{ width: 30, height: 26, padding: 0, border: 'none', borderRadius: 4, background: 'none', cursor: 'pointer' }}
            />
            <input
                type="range" min={0} max={1} step={0.01}
                value={style.opacity}
                onChange={e => setStyle(`${groupKey}.${item.key}`, { opacity: Number(e.target.value) })}
                style={{ width: 90 }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', width: 36, textAlign: 'right' }}>
                {Math.round(style.opacity * 100)}%
            </div>
            <button
                onClick={() => resetStyle(`${groupKey}.${item.key}`)}
                disabled={!isCustom}
                title="Reset to default"
                style={{
                    background: 'none', border: 'none', cursor: isCustom ? 'pointer' : 'default',
                    color: isCustom ? 'var(--text-tertiary)' : 'transparent', fontSize: 13, padding: 2, width: 18,
                }}
            >↺</button>
        </div>
    );
}

export default function SurfaceStyleSettings() {
    const { getStyle, setStyle, resetStyle, resetAll, overrides } = useSurfaceStyles();
    const [openGroup, setOpenGroup] = useState(null);
    const customCount = Object.keys(overrides).length;

    return (
        <div className="dg-panel" style={{ padding: '24px', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '1.2px', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                    Surface Visualization
                </div>
                {customCount > 0 && (
                    <button
                        onClick={resetAll}
                        style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}
                    >
                        Reset all ({customCount})
                    </button>
                )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 14 }}>
                Customize the color and opacity of each surface type, per assessment tab. Changes apply immediately across every map.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '60vh', overflowY: 'auto', paddingRight: 4 }}>
                {SURFACE_STYLE_GROUPS.map(group => {
                    const isOpen = openGroup === group.key;
                    return (
                        <div key={group.key} style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, overflow: 'hidden', flexShrink: 0 }}>
                            <div
                                onClick={() => setOpenGroup(isOpen ? null : group.key)}
                                style={{
                                    padding: '12px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    background: isOpen ? 'rgba(44,114,174,0.10)' : 'rgba(255,255,255,0.02)',
                                }}
                            >
                                <div style={{ fontSize: 13, fontWeight: 600, color: isOpen ? '#5F98C4' : 'var(--text-primary)' }}>
                                    {group.label}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{group.items.length} surfaces</span>
                                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
                                </div>
                            </div>
                            {isOpen && (
                                <div style={{ padding: '4px 14px 8px' }}>
                                    {group.items.map(item => {
                                        const fullKey = `${group.key}.${item.key}`;
                                        return (
                                            <StyleRow
                                                key={fullKey}
                                                groupKey={group.key}
                                                item={item}
                                                style={getStyle(fullKey)}
                                                setStyle={setStyle}
                                                resetStyle={resetStyle}
                                                isCustom={!!overrides[fullKey]}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
